// Stealth-Agent FE — isolated SSE AI stream hook (mirrors DashboardV2/useAiStream,
// kept inside the module for isolation). Consumes data: {type:'content'|'done'|...}.
import { useCallback, useRef, useState } from 'react';

export function useStealthAiStream(endpoint) {
  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const reset = useCallback(() => { setText(''); setError(null); }, []);

  const run = useCallback(async (body) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setStreaming(true); setError(null); setText('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
        signal: abortRef.current.signal,
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'content') setText((p) => p + d.text);
            else if (d.type === 'no_activity') { setError(d.message); setStreaming(false); }
            else if (d.type === 'error') setError(d.message);
            else if (d.type === 'done') setStreaming(false);
          } catch (_) { /* ignore */ }
        }
      }
      setStreaming(false);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
      setStreaming(false);
    }
  }, [endpoint]);

  return { text, streaming, error, run, reset };
}

// Markdown -> HTML for the AI reports. Supports headings, bold, bullet lists, AND
// GitHub-style pipe tables (rendered as clean, styled HTML tables — the old version
// turned table rows into flat <br> lines, which looked broken). No external dep.
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const inline = (s) => esc(s)
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/`([^`]+)`/g, '<code style="background:rgba(99,102,241,0.10);color:#4f46e5;padding:1px 5px;border-radius:5px;font-size:12px">$1</code>');

function renderTable(rows) {
  // rows: array of raw "| a | b |" lines (header, separator, then body)
  const cells = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const header = cells(rows[0]);
  const body = rows.slice(2).filter((r) => r.includes('|')).map(cells);
  const th = header.map((c) =>
    `<th style="text-align:left;padding:8px 12px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#94a3b8;font-weight:600;border-bottom:1px solid rgba(148,163,184,.18);white-space:nowrap">${inline(c)}</th>`).join('');
  const trs = body.map((r, i) => {
    const tds = r.map((c, ci) =>
      `<td style="padding:9px 12px;font-size:13px;color:#374151;vertical-align:top;${ci === 0 ? 'white-space:nowrap;font-variant-numeric:tabular-nums;color:#6366f1;font-weight:500' : ''}">${inline(c)}</td>`).join('');
    return `<tr style="${i % 2 ? 'background:rgba(148,163,184,.04)' : ''}">${tds}</tr>`;
  }).join('');
  return `<div style="overflow-x:auto;margin:10px 0;border:1px solid rgba(148,163,184,.16);border-radius:12px"><table style="width:100%;border-collapse:collapse"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`;
}

export function mdToHtml(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const out = [];
  let i = 0;
  let listOpen = false;
  const closeList = () => { if (listOpen) { out.push('</ul>'); listOpen = false; } };

  while (i < lines.length) {
    const line = lines[i];

    // Table block: a header row followed by a |---|---| separator.
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      closeList();
      const block = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { block.push(lines[i]); i += 1; }
      out.push(renderTable(block));
      continue;
    }

    if (/^### /.test(line)) { closeList(); out.push(`<h3 style="font-weight:700;margin:14px 0 6px;font-size:14px;color:#1f2937">${inline(line.slice(4))}</h3>`); i++; continue; }
    if (/^## /.test(line)) { closeList(); out.push(`<h2 style="font-weight:700;margin:18px 0 8px;font-size:16px;color:#111827;letter-spacing:-.01em">${inline(line.slice(3))}</h2>`); i++; continue; }
    if (/^# /.test(line)) { closeList(); out.push(`<h1 style="font-weight:800;margin:18px 0 10px;font-size:18px;color:#111827">${inline(line.slice(2))}</h1>`); i++; continue; }
    if (/^\s*[-*] /.test(line)) {
      if (!listOpen) { out.push('<ul style="margin:6px 0;padding-left:18px;list-style:disc">'); listOpen = true; }
      out.push(`<li style="margin:3px 0;line-height:1.6">${inline(line.replace(/^\s*[-*] /, ''))}</li>`);
      i++; continue;
    }
    if (line.trim() === '') { closeList(); out.push('<div style="height:8px"></div>'); i++; continue; }

    closeList();
    out.push(`<p style="margin:4px 0;line-height:1.7">${inline(line)}</p>`);
    i++;
  }
  closeList();
  return out.join('');
}
